/**
 * DateRangePicker - Reusable date range selection component
 * Includes quick range presets and manual date inputs
 */
import { ChevronDown } from 'lucide-react';
import { startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subYears } from 'date-fns';
import { toISODateString } from '@/client/lib/date';
import { Button } from '@/client/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';

export type QuickRange = 'today' | 'this-month' | 'last-month' | 'this-year' | 'last-year';

const QUICK_RANGE_LABELS: Record<QuickRange, string> = {
  'today': 'Today',
  'this-month': 'This Month',
  'last-month': 'Last Month',
  'this-year': 'This Year',
  'last-year': 'Last Year',
};

interface DateRangePickerProps {
  /** Start date in YYYY-MM-DD format */
  startDate: string;
  /** End date in YYYY-MM-DD format */
  endDate: string;
  /** Called when start date changes */
  onStartDateChange: (date: string) => void;
  /** Called when end date changes */
  onEndDateChange: (date: string) => void;
  /** Currently selected quick range (empty string if custom) */
  quickRange: string;
  /** Called when quick range changes */
  onQuickRangeChange: (range: string) => void;
  /** Available quick ranges (defaults to all) */
  availableRanges?: QuickRange[];
  /** Whether to show the "Today" option (some reports don't need it) */
  showToday?: boolean;
  /** Additional callback when any date changes (for resetting other state) */
  onDateChange?: () => void;
}

/**
 * Calculate date range for a given quick range preset
 */
export function getQuickRangeDates(range: QuickRange): { start: Date; end: Date } {
  const now = new Date();
  let start: Date;
  let end: Date;

  switch (range) {
    case 'today':
      start = now;
      end = now;
      break;
    case 'this-month':
      start = startOfMonth(now);
      end = endOfMonth(now);
      break;
    case 'last-month': {
      const lastMonth = subMonths(now, 1);
      start = startOfMonth(lastMonth);
      end = endOfMonth(lastMonth);
      break;
    }
    case 'this-year':
      start = startOfYear(now);
      end = endOfYear(now);
      break;
    case 'last-year': {
      const lastYear = subYears(now, 1);
      start = startOfYear(lastYear);
      end = endOfYear(lastYear);
      break;
    }
    default:
      start = now;
      end = now;
  }

  // Cap end date to today
  const today = new Date();
  if (end > today) {
    end = today;
  }

  return { start, end };
}

export function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  quickRange,
  onQuickRangeChange,
  availableRanges,
  showToday = true,
  onDateChange,
}: DateRangePickerProps) {
  const today = toISODateString(new Date());

  // Determine which ranges to show
  const ranges = availableRanges ?? (
    showToday
      ? (['today', 'this-month', 'last-month', 'this-year', 'last-year'] as QuickRange[])
      : (['this-month', 'last-month', 'this-year', 'last-year'] as QuickRange[])
  );

  const handleQuickRangeSelect = (range: QuickRange) => {
    const { start, end } = getQuickRangeDates(range);
    onStartDateChange(toISODateString(start));
    onEndDateChange(toISODateString(end));
    onQuickRangeChange(range);
    onDateChange?.();
  };

  const handleStartDateChange = (value: string) => {
    onStartDateChange(value);
    onQuickRangeChange(''); // Clear quick range when manually editing
    onDateChange?.();
  };

  const handleEndDateChange = (value: string) => {
    onEndDateChange(value);
    onQuickRangeChange(''); // Clear quick range when manually editing
    onDateChange?.();
  };

  return (
    <>
      {/* Quick Range Select */}
      <div className="flex flex-col gap-2 flex-1 min-w-[140px]">
        <label className="text-sm font-medium">Quick Range</label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-10 w-full justify-between font-normal">
              {quickRange ? QUICK_RANGE_LABELS[quickRange as QuickRange] : 'Select...'}
              <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
            {ranges.map((range) => (
              <DropdownMenuItem key={range} onClick={() => handleQuickRangeSelect(range)}>
                {QUICK_RANGE_LABELS[range]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Start Date */}
      <div className="flex flex-col gap-2 flex-1 min-w-[150px]">
        <label className="text-sm font-medium">Start Date</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => handleStartDateChange(e.target.value)}
          max={endDate || today}
          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {/* End Date */}
      <div className="flex flex-col gap-2 flex-1 min-w-[150px]">
        <label className="text-sm font-medium">End Date</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => handleEndDateChange(e.target.value)}
          min={startDate}
          max={today}
          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
    </>
  );
}
